import { ref, type Ref } from 'vue'

// Ручной выбор темы поверх системной. «Системная» — атрибута data-theme нет,
// тему рулит CSS (@media prefers-color-scheme). «Светлая»/«Тёмная» — ставим
// data-theme на <html> (перебивает медиа-запрос, см. application.css) и сохраняем
// в localStorage. Ранний init (application.ts) применяет сохранённый выбор до
// монтирования Vue. Синглтон на модуле: один источник истины для темы приложения.
export type ThemeMode = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'theme'
const CYCLE: ThemeMode[] = ['system', 'light', 'dark']

function readStored(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved === 'light' || saved === 'dark' ? saved : 'system'
  } catch {
    // localStorage недоступен (или SSR) — считаем системной.
    return 'system'
  }
}

function applyToDom(next: ThemeMode): void {
  if (typeof document === 'undefined') return
  if (next === 'system') delete document.documentElement.dataset.theme
  else document.documentElement.dataset.theme = next
}

const mode = ref<ThemeMode>(readStored())

export function useTheme(): {
  mode: Ref<ThemeMode>
  setTheme: (next: ThemeMode) => void
  cycle: () => void
} {
  function setTheme(next: ThemeMode): void {
    mode.value = next
    applyToDom(next)
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // localStorage недоступен — тема применится к DOM, но не переживёт перезагрузку.
    }
  }

  function cycle(): void {
    const index = CYCLE.indexOf(mode.value)
    setTheme(CYCLE[(index + 1) % CYCLE.length])
  }

  return { mode, setTheme, cycle }
}
